/**
 * ThemeModeToggle (ported from dink-admin DNKADM-0266) — color-mode control
 * used inline in the profile panel (`variant="segmented"`) and, when needed,
 * as a menu (`variant="menu"`): Light / Dark / System. Driven by
 * `useThemeMode()`; the selection persists via the ThemeModeProvider
 * (`tmm_reseller_admin.themeMode`).
 */
import { useState } from 'react';
import { IconButton, ListItemIcon, Menu, MenuItem, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useThemeMode, type ThemeMode } from '@/context/theme-mode-context';
import { Icon } from '@/theme';

export type ThemeModeToggleVariant = 'menu' | 'segmented';

export interface ThemeModeToggleProps {
  variant?: ThemeModeToggleVariant;
}

interface ModeOption {
  value: ThemeMode;
  labelKey: 'theme.light' | 'theme.dark' | 'theme.system';
  icon: string;
}

const SYSTEM_OPTION: ModeOption = { value: 'system', labelKey: 'theme.system', icon: 'contrast' };

const MODE_OPTIONS: readonly ModeOption[] = [
  { value: 'light', labelKey: 'theme.light', icon: 'light_mode' },
  { value: 'dark', labelKey: 'theme.dark', icon: 'dark_mode' },
  SYSTEM_OPTION,
];

export function ThemeModeToggle({ variant = 'menu' }: ThemeModeToggleProps) {
  const { t } = useTranslation();
  const { themeMode, setThemeMode } = useThemeMode();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const current = MODE_OPTIONS.find((option) => option.value === themeMode) ?? SYSTEM_OPTION;

  if (variant === 'segmented') {
    return (
      <ToggleButtonGroup
        exclusive
        size="small"
        value={themeMode}
        onChange={(_event, next: ThemeMode | null) => {
          if (!next) return;
          setThemeMode(next);
        }}
        aria-label={t('theme.label')}
      >
        {MODE_OPTIONS.map((option) => (
          <ToggleButton key={option.value} value={option.value} aria-label={t(option.labelKey)}>
            {t(option.labelKey)}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    );
  }

  return (
    <>
      <Tooltip title={t('theme.label')}>
        <IconButton
          aria-label={t('theme.label')}
          aria-haspopup="menu"
          onClick={(e) => setAnchorEl(e.currentTarget)}
        >
          <Icon name={current.icon} size="lg" />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        {MODE_OPTIONS.map((option) => (
          <MenuItem
            key={option.value}
            selected={option.value === themeMode}
            onClick={() => {
              setAnchorEl(null);
              setThemeMode(option.value);
            }}
          >
            <ListItemIcon>
              <Icon name={option.icon} size="md" />
            </ListItemIcon>
            {t(option.labelKey)}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
